/**
 * env.ts — validación de variables de entorno con Zod.
 *
 * Patrón canónico armazon-i10 (capa seguridad sub-capa 2: validación inputs).
 * Cualquier env var que entra al sistema se valida aquí, en la frontera.
 * En el interior se asume que `env.X` tiene el tipo correcto.
 *
 * Si una env var es inválida o falta, la app NO arranca (fail fast).
 *
 * Para añadir nueva env var:
 *   1. Añadir al schema EnvSchema.
 *   2. Añadir a .env.example con comentario.
 *   3. Si es secret en producción: NO añadir valor real a .env.example.
 *   4. Si es opcional: marcar .optional() + dar default si aplica.
 */

import { z } from 'zod';

const EnvSchema = z.object({
  // Modo de ejecución
  MODE: z.enum(['development', 'test', 'production']).default('development'),

  // Supabase (obligatorio)
  VITE_SUPABASE_URL: z
    .string()
    .url('VITE_SUPABASE_URL debe ser URL válida (ej: https://supabase.ideas10.com)'),
  VITE_SUPABASE_ANON_KEY: z
    .string()
    .min(20, 'VITE_SUPABASE_ANON_KEY parece inválida (esperado JWT largo)'),

  // n8n webhooks (opcional, según proyecto)
  VITE_N8N_WEBHOOK_URL: z
    .string()
    .url()
    .optional(),

  // Sentry / observability (opcional)
  VITE_SENTRY_DSN: z.string().url().optional(),

  // Feature flags (opcional)
  VITE_ENABLE_ANALYTICS: z.coerce.boolean().default(false),
  VITE_ENABLE_DEBUG_LOGS: z.coerce.boolean().default(false),
});

export type Env = z.infer<typeof EnvSchema>;

/**
 * Parsea import.meta.env con el schema. Si falla, lanza error con detalle.
 * Llamado UNA VEZ al arrancar la app.
 */
function parseEnv(): Env {
  const result = EnvSchema.safeParse(import.meta.env);

  if (!result.success) {
    const flat = result.error.flatten();
    const messages = Object.entries(flat.fieldErrors).map(
      ([key, errors]) => `  - ${key}: ${(errors ?? []).join(', ')}`
    );
    throw new Error(
      [
        'Configuración de entorno inválida. Revisa tu .env.local:',
        ...messages,
        '',
        'Copia .env.example si aún no lo hiciste:',
        '  cp .env.example .env.local',
      ].join('\n')
    );
  }

  return result.data;
}

export const env = parseEnv();

/**
 * Helper: verifica si estamos en producción.
 * Uso: `if (isProd()) { ... }`.
 */
export const isProd = () => env.MODE === 'production';
export const isDev = () => env.MODE === 'development';
export const isTest = () => env.MODE === 'test';
